import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { accountService } from './service';

/**
 * Server state for the account cards.
 *
 * One root key, so changing the password - which signs out every other
 * device - refreshes the session lists in the same breath.
 */
export const accountKeys = {
  all: ['account'] as const,
  sessions: () => [...accountKeys.all, 'sessions'] as const,
  team: () => [...accountKeys.all, 'team-sessions'] as const,
};

export function useSessions() {
  return useQuery({
    queryKey: accountKeys.sessions(),
    queryFn: ({ signal }) => accountService.sessions(signal),
    staleTime: 30_000,
  });
}

export function useTeamSessions(enabled: boolean) {
  return useQuery({
    queryKey: accountKeys.team(),
    queryFn: ({ signal }) => accountService.teamSessions(signal),
    enabled,
    staleTime: 30_000,
  });
}

function useAccountMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => client.invalidateQueries({ queryKey: accountKeys.all }),
  });
}

export const useChangePassword = () => useAccountMutation(accountService.changePassword);
export const useEndSession = () => useAccountMutation(accountService.endSession);
export const useEndOtherSessions = () =>
  useAccountMutation((_: void) => accountService.endOtherSessions());
export const useEndTeamSession = () => useAccountMutation(accountService.endTeamSession);
export const useEndMemberSessions = () => useAccountMutation(accountService.endMemberSessions);
